import { ChartType } from 'types/CodegenReportTypes';
import { create } from 'zustand';

// THIS FILE IS NOT SUPPPOSED TO BE MODIFIED
// Please be cautious when modifying this file, since it affects the entire project and can easily break things.
// Type definitions here are a little crazy, but thanks to that we get full type safety across the project.

type RequiredStoreKeys = 'searchCriteria' | 'searchChips' | 'specialConfigOptions' | 'selectedConfgValues' | 'layout' | 'chartData';

type BaseState<ReportSpecificStore extends Record<RequiredStoreKeys, any>> = {
  canGetNewData: boolean;
  isEditMode: boolean;
  editingChart: ChartType | undefined;
  tempLayout: ReportSpecificStore['layout'];
};

type BaseActions<ReportSpecificStore extends Record<RequiredStoreKeys, any>> = {
  setCanGetNewData: (value: boolean) => void;
  setIsEditMode: (value: boolean) => void;
  setEditingChart: (value: ChartType | undefined) => void;
  setTempLayout: (value: ReportSpecificStore['layout']) => void;
  resetStore: () => void;
  setPartialSearchCriteria: (partialSearchCriteria: Partial<ReportSpecificStore['searchCriteria']>) => void;
  setSearchChips: (value: Exclude<ReportSpecificStore['searchChips'], undefined>) => void;
  setPartialSearchChips: (partialSearchChips: Partial<ReportSpecificStore['searchChips']>) => void;
  setSpecialConfigOptions: (value: Exclude<ReportSpecificStore['specialConfigOptions'], undefined>) => void;
  setSelectedConfigValue: <T extends Exclude<ReportSpecificStore['selectedConfgValues'], undefined>, Key extends keyof T>(
    key: Key,
    value: T[Key]
  ) => void;
  setLayout: (value: Exclude<ReportSpecificStore['layout'], undefined>) => void;
  setChartData: (value: Exclude<ReportSpecificStore['chartData'], undefined>) => void;
};

export const generateStore = <ReportSpecificStore extends Record<RequiredStoreKeys, any>>(defaultValues: ReportSpecificStore) => {
  type Store = ReportSpecificStore & BaseState<ReportSpecificStore> & { actions: BaseActions<ReportSpecificStore> };

  return create<Store>()((set) => ({
    ...defaultValues,
    isEditMode: false,
    editingChart: undefined,
    tempLayout: defaultValues.layout,
    canGetNewData: true,

    actions: {
      setIsEditMode: (isEditMode) => set(() => ({ isEditMode, editingChart: undefined }) as Partial<Store>),

      setEditingChart: (editingChart) => set(() => ({ editingChart }) as Partial<Store>),

      setTempLayout: (tempLayout) => set(() => ({ tempLayout }) as Partial<Store>),

      setCanGetNewData: (canGetNewData) => set(() => ({ canGetNewData }) as Partial<Store>),

      resetStore: () =>
        set(
          () =>
            ({
              ...defaultValues,
              isEditMode: false,
              editingChart: undefined,
              tempLayout: defaultValues.layout,
              canGetNewData: true,
            }) as unknown as Partial<Store>
        ),

      setPartialSearchCriteria: (partialSearchCriteria) =>
        set((state) => ({ searchCriteria: { ...state.searchCriteria, ...partialSearchCriteria } }) as Partial<Store>),

      setSearchChips: (searchChips) => set(() => ({ searchChips }) as Partial<Store>),

      setPartialSearchChips: (partialSearchChips) =>
        set((state) => ({ searchChips: { ...state.searchChips, ...partialSearchChips } }) as Partial<Store>),

      setSpecialConfigOptions: (specialConfigOptions) => set(() => ({ specialConfigOptions }) as Partial<Store>),

      setSelectedConfigValue: (key, value) =>
        set((state) => ({ selectedConfgValues: { ...state.selectedConfgValues, [key]: value } }) as Partial<Store>),

      setLayout: (layout) => set(() => ({ layout, tempLayout: layout }) as Partial<Store>),

      setChartData: (chartData) => set(() => ({ chartData }) as Partial<Store>),
    },
  }));
};
